import { ReactNode, useState } from 'react';
import { Carousel as CarouselZDS } from '@zapier/design-system';
import styled from '@emotion/styled';
import { mediaQueries } from '../../constants';
import { useViewportSize } from '../../hooks/useViewportSize';

type Props = {
  children: ReactNode;
  onActiveSlideChange: (currentSlide: number, previousSlide: number) => void;
};
export const Carousel = ({ children, onActiveSlideChange }: Props) => {
  const { width } = useViewportSize();
  const isMobile = width === 'extraSmall' || width === 'small';

  const slideWidth = isMobile ? 300 : 650;
  const [arrowOffset, setArrowOffset] = useState('50%');
  const [showArrows, setShowArrows] = useState(false);

  const containerRef = (node: HTMLDivElement) => {
    if (!node) return;
    const image = node.querySelector('img');

    if (!image) {
      setShowArrows(!isMobile);
      return;
    }

    const handleLoad = () => {
      if (image.clientHeight === 0) return;
      setArrowOffset(`${image.clientHeight / 2}px`);
      setShowArrows(!isMobile);
    };

    image.onload = handleLoad;

    image.onerror = () => {
      setShowArrows(!isMobile);
    };

    /** handleLoad is sometimes attached after `onLoad` has already fired.
     *  So, we want to fire it again here and use the early return when image.clientHeight is 0
     *  to avoid the arrows it sliding in from the top.
     */
    handleLoad();
  };

  return (
    <CarouselContainer data-testid="carousel" ref={containerRef}>
      <CarouselZDS
        arrowOffset={arrowOffset}
        onActiveSlideChange={onActiveSlideChange}
        paginatorStyle="indicator"
        showArrows={showArrows}
        slideWidth={slideWidth}
      >
        {children}
      </CarouselZDS>
      <CarouselOverlay />
    </CarouselContainer>
  );
};

const CarouselContainer = styled.div`
  position: relative;
`;

const CarouselOverlay = styled.div`
  ${mediaQueries.desktop} {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 423px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 1) 100%
    );
  }
`;
